import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Menu, Plus, Trash2, X } from 'lucide-react';
import { SUCCESS_MESSAGE } from '@/utils/constants';
import { Select, Tree, TimePicker } from 'antd';
import { Button } from '../ui/button';
import { useAutomations } from '@/api/hooks/useAutomation';
import { createProfile, editProfile } from '@/api/profile';
import dayjs from 'dayjs';
import { blueprints } from '@/pages/autopilot/create';
import { cn } from '@/lib/utils';
import { Blueprint } from '@/types';

interface AutomationProfile {
  automation_id: number;
  start_time: string;
  end_time: string;
}

interface TitleRenderProps {
  idx: number;
  data: AutomationProfile;
  options: { value: number; label: string, blueprint: Blueprint }[];
  onChange: (value: any, key: string) => void;
  onDelete: () => void;
  disabled: boolean;
}

interface ProfileData {
  id?: number;
  name?: string;
  automation_profiles?: {
    automation: {
      id: number;
      name: string;
    };
    start_time: string;
    end_time: string;
  }[];
}

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  data?: ProfileData;
  message: any;
  edit?: boolean;
  refetch: () => void;
}

const TitleRender: React.FC<TitleRenderProps> = ({ idx, data, options, onChange, onDelete, disabled }) => {
  return (
    <div className='w-full flex p-2 flex-row border border-light-grey rounded-[10px] bg-white items-center gap-2'>
      <Menu className='w-4 h-4' />
      <div className='flex flex-col gap-1'>
        <p className='paragraph-10 text-dark-grey'>Priority</p>
        <input
          disabled
          value={idx + 1}
          className="w-[80px] h-[32px] px-2 text-center text-xs text-[#B4B4B4] border border-white-blue rounded-lg focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed"
        />
      </div>
      <div className='flex flex-col gap-1 grow'>
        <p className='paragraph-10 text-dark-grey'>Select Automation</p>
        <Select
          options={options}
          value={data?.automation_id}
          disabled={disabled}
          optionRender={(option, v) => {
            const blueprint = blueprints[option?.data?.blueprint?.id - 1]
            return (
              <div className='flex ant'>
                {blueprint && (
                  <div className="flex items-center gap-2 mr-1.5 ant">
                    <div className={cn("w-4 h-4 rounded flex items-center justify-center ant", blueprint.color)}>
                      {blueprint.icon}
                    </div>
                  </div>
                )}
                <p className='ant'>{option?.label}</p>
              </div>
            )
          }}
          labelRender={(props) => {
            const option = options?.find(option => option?.value === props.value)
            const blueprint = blueprints[option?.blueprint?.id - 1]
            return (
              <div className='flex ant'>
                {blueprint && (
                  <div className="flex items-center gap-2 mr-1.5 ant">
                    <div className={cn("w-4 h-4 rounded flex items-center justify-center ant", blueprint.color)}>
                      {blueprint.icon}
                    </div>
                  </div>
                )}
                <p className='ant'>{option?.label}</p>
              </div>
            )
          }}
          onChange={(e) => onChange(e, 'automation_id')}
        />
      </div>
      <div className='flex flex-col gap-1'>
        <p className='paragraph-10 text-dark-grey'>Start</p>
        <TimePicker
          placeholder='HH:MM'
          format='HH:mm'
          needConfirm={false}
          showNow={false}
          value={data?.start_time ? dayjs(data?.start_time, 'HH:mm') : null}
          disabled={disabled}
          onChange={(e) => e && onChange(e.format('HH:mm'), 'start_time')}
          className="w-[150px] h-[32px] px-2 text-xs text-black border border-white-blue rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <div className='flex flex-col gap-1'>
        <p className='paragraph-10 text-dark-grey'>End</p>
        <TimePicker
          placeholder='HH:MM'
          format='HH:mm'
          needConfirm={false}
          showNow={false}
          value={data?.end_time ? dayjs(data?.end_time, 'HH:mm') : null}
          disabled={disabled}
          onChange={(e) => e && onChange(e.format('HH:mm'), 'end_time')}
          className="w-[150px] h-[32px] px-2 text-xs text-black border border-white-blue rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <Button
        disabled={disabled}
        variant="ghost"
        size="sm"
        className="h-4.5 w-4.5 p-1 rounded hover:bg-red-50"
        onClick={onDelete}
      >
        <Trash2 className="h-3.5 w-3.5 text-danger" />
      </Button>
    </div>
  );
};

export function ProfileModal({ isOpen, onClose, data, message, edit = false, refetch }: ProfileModalProps) {
  const [isEdit, setIsEdit] = useState(edit);
  const [name, setName] = useState(data?.name || '');
  const [automations, setAutomations] = useState<AutomationProfile[]>([]);

  const { data: automationsData } = useAutomations();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (target.className && typeof target.className === 'string' && !target.className.includes('ant')) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const isCreate = !data;

  const onDrop = (info: any) => {
    const newAutomations = [...automations];
    const [movedElement] = newAutomations.splice(info.dragNode.key, 1);
    newAutomations.splice(info.dropPosition - Number(info.dropToGap), 0, movedElement);
    setAutomations(newAutomations);
  };

  const handleOnClick = async () => {
    const profileData = {
      name,
      automation_profiles: automations?.map((v, idx) => ({
        ...v,
        priority: idx + 1,
      })),
    };

    try {
      if (isCreate) {
        await createProfile(profileData);
      } else if (isEdit && data) {
        await editProfile(data.id, profileData);
      } else {
        setIsEdit(true);
        return;
      }

      refetch();
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
      onClose();
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  };

  useEffect(() => {
    if (!data?.automation_profiles) {
      setAutomations([]);
    } else {
      setAutomations(data.automation_profiles?.map((v) => ({
        automation_id: v.automation.id,
        start_time: v.start_time,
        end_time: v.end_time,
      })));
    }
  }, [data]);

  useEffect(() => {
    setIsEdit(edit);
  }, [edit]);

  if (!isOpen) return null;

  const disabled = !isCreate && !isEdit;
  const automationOptions = automationsData?.map(v => ({ value: v.id, label: v.name, blueprint: v.blueprint })) || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center !mt-0">
      <div ref={modalRef} className="flex flex-col gap-3 w-[800px] min-h-[550px] h-[550px] py-4 bg-bg rounded-xl shadow-lg backdrop-blur-[20px] shadow-popup">
        {/* Header */}
        <div className="flex justify-between px-4 items-center border-light-grey">
          {isEdit ? (
            <input
              placeholder='Profile Name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-[180px] h-[32px] px-2 text-xs border border-white-blue rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
            />
          ) : (
            <h2 className="text-dark-blue heading-5">{data?.name || 'Create Profile'}</h2>
          )}
          <button
            onClick={onClose}
            className="text-primary hover:text-dark-blue transition-colors"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isCreate && (
          <div className='flex flex-row px-4 items-center gap-2'>
            <p className='heading-7 text-dark-blue'>Profile Name</p>
            <input
              placeholder='Profile Name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-[180px] h-[32px] px-2 text-xs border border-white-blue rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        )}

        <div className='w-full px-4 flex py-1.5 grow  overflow-y-scroll'>
          <Tree
            className="draggable-tree bg-bg w-full"
            draggable={{ icon: false }}
            switcherIcon={() => <div className='w-0' />}
            blockNode
            onDrop={onDrop}
            showIcon={false}
            disabled={disabled}
            treeData={automations?.map((v, idx) => ({
              key: idx,
              title: (
                <TitleRender
                  idx={idx}
                  data={v}
                  options={automationOptions}
                  disabled={disabled}
                  onChange={(e, key) => {
                    const newAutomations = [...automations];
                    newAutomations[idx] = {
                      ...newAutomations[idx],
                      [key]: e
                    };
                    setAutomations(newAutomations);
                  }}
                  onDelete={() => {
                    const newAutomations = [...automations];
                    newAutomations.splice(idx, 1);
                    setAutomations(newAutomations);
                  }}
                />
              )
            }))}
          />
        </div>

        <div className='w-full flex px-4 flex-row border-t border-whiteblue items-center gap-2 justify-between pt-3'>
          <Button
            disabled={disabled}
            variant="outline"
            size="md"
            className="bg-white text-primary border-primary"
            onClick={() => setAutomations([
              ...automations,
              { automation_id: undefined, start_time: '', end_time: '' } as AutomationProfile,
            ])}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            <p className="button-s">Add Automation</p>
          </Button>
          <Button
            variant="default"
            size="md"
            className="bg-primary text-white"
            disabled={isCreate && !name}
            onClick={handleOnClick}
          >
            <p className="button-s">{isCreate ? 'Create Profile' : isEdit ? 'Save Changes' : 'Edit'}</p>
          </Button>
        </div>
      </div>
    </div>
  );
}